import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import DocumentsList from "./DocumentsList";
import DocumentFilters from "./DocumentFilters";
import DocumentSearch from "./DocumentSearch";
import { DeleteDocumentApi, GetDocuments, SearchDocuments, UpdateDocumentApi } from "../apis/documents";
import { DocumentFiltersState, DocumentRecord } from "./documentTypes";

const defaultFilters: DocumentFiltersState = {
  q: "",
  category: "",
  uploaded_by: "",
  file_type: "",
  tag: "",
  is_pinned: "",
  is_archived: "false",
  is_active: "true",
  date_filter: "",
  date_from: "",
  date_to: "",
  sort_by: "newest",
};

const getRole = () => {
  try {
    return (JSON.parse(localStorage.getItem("data") || "{}")?.Role || "").toLowerCase();
  } catch {
    return "";
  }
};

const toApiFilters = (filters: DocumentFiltersState, limit: number, page: number) => ({
  ...filters,
  limit,
  offset: (page - 1) * limit,
});

export default function DocumentsDashboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DocumentFiltersState>(defaultFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(18);
  const [total, setTotal] = useState(0);

  const role = getRole();
  const canDelete = ["super_admin", "admin"].includes(role);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(filters.q), 350);
    return () => window.clearTimeout(handle);
  }, [filters.q]);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const requestFilters = toApiFilters({ ...filters, q: debouncedSearch }, limit, page);
      const response = debouncedSearch ? await SearchDocuments(requestFilters) : await GetDocuments(requestFilters);
      const payload = response?.data || {};
      setDocuments(payload.documents || []);
      setTotal(payload.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load documents");
      setDocuments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, limit, page]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const updateFilters = (updates: Partial<DocumentFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    setPage(1);
  };

  const stats = useMemo(() => ({
    total,
    pinned: documents.filter((doc) => doc.is_pinned).length,
    archived: documents.filter((doc) => doc.is_archived).length,
  }), [documents, total]);

  const handleDelete = async (document: DocumentRecord) => {
    if (!window.confirm(`Delete "${document.title}"?`)) return;
    try {
      await DeleteDocumentApi(document.id);
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete document");
    }
  };

  const handleTogglePin = async (document: DocumentRecord) => {
    try {
      await UpdateDocumentApi(document.id, { is_pinned: !document.is_pinned });
      toast.success(document.is_pinned ? "Document unpinned" : "Document pinned");
      fetchDocuments();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update pin state");
    }
  };

  return (
    <div className="mt-8 space-y-5 pb-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-blue-300">Admin Knowledge Base</p>
          <h1 className="mt-1 text-2xl font-black text-white md:text-3xl">Documents</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={fetchDocuments} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800">
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={() => navigate("/documents/upload")} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">
            <Plus size={16} /> Upload Document
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4">
          <p className="text-xs text-slate-400">Matched documents</p>
          <p className="mt-1 text-2xl font-black text-white">{stats.total}</p>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4">
          <p className="text-xs text-slate-400">Pinned on this page</p>
          <p className="mt-1 text-2xl font-black text-amber-300">{stats.pinned}</p>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-4">
          <p className="text-xs text-slate-400">Archived on this page</p>
          <p className="mt-1 text-2xl font-black text-slate-200">{stats.archived}</p>
        </div>
      </div>

      <DocumentSearch value={filters.q} onChange={(q) => updateFilters({ q })} />
      <DocumentFilters filters={filters} onChange={updateFilters} onReset={() => { setFilters(defaultFilters); setPage(1); }} />

      <DocumentsList documents={documents} loading={loading} canDelete={canDelete} onDelete={handleDelete} onTogglePin={handleTogglePin} />

      <div className="flex flex-col items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-4 py-3 sm:flex-row">
        <p className="text-sm text-slate-400">Page {page} of {totalPages} | {total} documents</p>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-slate-800">
            <ChevronLeft size={16} /> Previous
          </button>
          <button disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-slate-800">
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
